import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
  limitShift,
  size,
} from '@floating-ui/dom';

import {
  roundByDPR,
} from './FloatingPopover.helpers.js';

import {
  nextMicroTask,
} from '../../utilities/queue.js';

/**
 * @typedef {import('@floating-ui/dom').Strategy} Strategy
 * @typedef {import('@floating-ui/dom').Placement} Placement
 * @typedef {import('@floating-ui/dom').Middleware} Middleware
 */
export class FloatingPopover extends HTMLElement {
  /**
   * @param {string} tagName
   */
  static define(tagName = 'floating-popover') {
    if ('customElements' in window) {
      customElements.define(tagName, FloatingPopover);
    }
  }

  /**
   * @returns {string[]}
   */
  static get observedAttributes() {
    return [
      'strategy',
      'placement',
      'distance',
      'flip',
      'active',
    ];
  }

  /**
   * @returns {?HTMLElement}
   */
  get anchorElement() {
    return this.#anchorElement;
  }

  /**
   * @param {?HTMLElement} value
   */
  set anchorElement(value) {
    this.#anchorElement = value;
  }

  /** @returns {?string} */
  get anchor() {
    return this.getAttribute('anchor');
  }

  /** @param {?string} value */
  set anchor(value) {
    if (value !== null) {
      this.setAttribute('anchor', value);
    } else {
      this.removeAttribute('anchor');
    }
  }

  /** @returns {Strategy} */
  get strategy() {
    return /** @type {Strategy} */ (
      this.getAttribute('strategy') ?? 'absolute'
    );
  }

  /** @param {?Strategy} value */
  set strategy(value) {
    if (value !== null) {
      this.setAttribute('strategy', value);
    } else {
      this.removeAttribute('strategy');
    }
  }

  /** @returns {Placement} */
  get placement() {
    return /** @type {Placement} */ (
      this.getAttribute('placement') ?? 'bottom'
    );
  }

  /** @param {?Placement} value */
  set placement(value) {
    if (value !== null) {
      this.setAttribute('placement', value);
    } else {
      this.removeAttribute('placement');
    }
  }

  /** @returns {number} */
  get distance() {
    return parseFloat(this.getAttribute('distance') ?? '0');
  }

  /** @param {?number} value */
  set distance(value) {
    if (value !== null) {
      this.setAttribute('distance', value.toString());
    } else {
      this.removeAttribute('distance');
    }
  }

  /** @returns {number} */
  get padding() {
    return parseFloat(this.getAttribute('padding') ?? '0');
  }

  /** @param {?number} value */
  set padding(value) {
    if (value !== null) {
      this.setAttribute('padding', value.toString());
    } else {
      this.removeAttribute('padding');
    }
  }

  /** @returns {boolean} */
  get flip() {
    return this.hasAttribute('flip');
  }

  /** @param {?boolean} value */
  set flip(value) {
    this.toggleAttribute('flip', !!value);
  }

  /** @returns {boolean} */
  get shift() {
    return this.hasAttribute('shift');
  }

  /** @param {?boolean} value */
  set shift(value) {
    this.toggleAttribute('shift', !!value);
  }

  /** @returns {boolean} */
  get active() {
    return this.hasAttribute('active');
  }

  /** @param {?boolean} value */
  set active(value) {
    this.toggleAttribute('active', !!value);
  }

  /**
   * @type {boolean}
   */
  #isInitialized = false;

  /**
   * @type {boolean}
   */
  #isFloating = false;

  /**
   * @type {?HTMLElement}
   */
  #anchorElement = null;

  /**
   * @type {?Promise<void>}
   */
  #updating = null;

  /**
   * @type {?function(): void}
   */
  #stopFloating = null;

  constructor() {
    super();
    this.handleToggleEvent = this.handleToggleEvent.bind(this);
  }

  /**
   * @returns {void}
   */
  connectedCallback() {
    this.#anchorElement = document.getElementById(this.anchor);

    this.addEventListener('toggle', this.handleToggleEvent);
    this.#startAutoUpdate();

    this.#isInitialized = true;
  }

  /**
   * @returns {void}
   */
  disconnectedCallback() {
    this.#anchorElement = null;

    this.#stopAutoUpdate();
    this.removeEventListener('toggle', this.handleToggleEvent);

    this.#isInitialized = false;
  }

  /**
   * @param {string} name
   * @param {?string} oldValue
   * @param {?string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (!this.#isInitialized || oldValue === newValue) {
      return;
    }

    if (name === 'active') {
      this.#toggleAutoUpdate(newValue !== null);
    } else {
      this.requestUpate();
    }
  }

  /**
   * @returns {Promise<void>}
   */
  async requestUpate() {
    return this.#updating ??= nextMicroTask().then(async () => {
      await this.update();
      this.#updating = null;
    });
  }

  /**
   * @returns {Promise<void>}
   */
  async update() {
    const anchor = this.anchorElement;

    if (! anchor) {
      return;
    }

    const {x, y} = await computePosition(anchor, this, {
      strategy: this.strategy,
      placement: this.placement,
      middleware: [
        this.distance > 0 && offset({
          mainAxis: this.distance,
        }),
        this.flip && flip({
          padding: this.padding,
        }),
        this.shift && shift({
          padding: this.padding,
          limiter: limitShift(),
        }),
        this.shift && size({
          padding: this.padding,
          apply: ({availableWidth, availableHeight}) => {
            this.style.setProperty('--available-inline-size', `${Math.max(0, availableWidth)}px`);
            this.style.setProperty('--available-block-size', `${Math.max(0, availableHeight)}px`);
          },
        }),
      ],
    });

    Object.assign(this.style, {
      position: this.strategy,
      top: 0,
      right: 'auto',
      bottom: 'auto',
      left: 0,
      transform: `translate(${roundByDPR(x)}px, ${roundByDPR(y)}px)`,
      ...(window.devicePixelRatio >= 1.5 && {willChange: 'transform'}),
    });
  }

  /**
   * @param {Event} event
   */
  handleToggleEvent(event) {
    this.active = /** @type {CustomEvent} */ (event).detail?.newState === 'open';
  }

  /**
   * @param {boolean} [force]
   */
  #toggleAutoUpdate(force = undefined) {
    const active = force != null ? force : !this.#isFloating;

    if (active) {
      this.#startAutoUpdate();
    } else {
      this.#stopAutoUpdate();
    }
  }

  /**
   * @returns {void}
   */
  #startAutoUpdate() {
    if (this.#isFloating || !this.anchorElement) {
      return;
    }

    this.#stopFloating = autoUpdate(this.anchorElement, this, this.update.bind(this));
    this.#isFloating = true;
  }

  /**
   * @returns {void}
   */
  #stopAutoUpdate() {
    if (!this.#isFloating) {
      return;
    }

    this.#stopFloating?.();
    this.#isFloating = false;
  }
}
